import React, { FC, useEffect, useRef, useState } from "react";

import { Controller } from "react-hook-form";
import type { Scrollbar as BaseScrollbar } from "smooth-scrollbar/scrollbar";

import ComponentScrollbar from "../ComponentScrollbar";
import Image, { ICONS } from "../Image";

import { StyledErrorLabel, StyledLabel } from "../Flex";

import useOutsideClick from "helpers/hooks/useOutsideClick";

import { ComboBoxProps, KEY, OptionProps } from "./ComboBox.constants";
import {
  StyledIconContainer,
  StyledInnerContainer,
  StyledOption,
  StyledOptionsContainer,
  StyledOuterContainer,
  StyledSelectContainer,
  StyledSelectedValue,
  SelectScrollWrapper,
} from "./ComboBox.styles";

const ComboBox: FC<ComboBoxProps> = ({
  label,
  name,
  error,
  options = [],
  disabled,
  control,
  onChange,
  onError,
  defaultValue,
}) => {
  const selectScrollWrapperRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const refOuter = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<BaseScrollbar | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<OptionProps>();

  useOutsideClick(selectScrollWrapperRef, setIsOpen);

  const handleSelectClick = (): void => setIsOpen((prevState) => !prevState);

  const handleOptionClick = (index: number): void => {
    setSelectedIndex(index);
    setIsOpen(false);
  };

  const pressKeyHandler = (
    e: React.KeyboardEvent<HTMLDivElement>,
    onControllerChange?: (...event: any[]) => void
  ): void => {
    if (e.key === KEY.ARROW_DOWN) {
      e.preventDefault();

      setSelectedIndex((prevState) =>
        prevState !== options?.length - 1 ? prevState + 1 : prevState
      );
    } else if (e.key === KEY.ARROW_UP) {
      e.preventDefault();

      setSelectedIndex((prevState) =>
        prevState !== 0 ? prevState - 1 : prevState
      );
    } else if (e.key === KEY.ENTER) {
      const findItem = options.find((item, index) => index === selectedIndex);

      handleOptionClick(selectedIndex);
      if (findItem) {
        setSelectedItem(findItem);
        onControllerChange && onControllerChange(findItem.value);
        onChange && onChange(findItem);
      }
    }
  };

  useEffect(() => {
    if (scrollbarRef.current?.size) {
      scrollbarRef.current.size = {
        container: { width: 50, height: 50 },
        content: { width: 50, height: 50 },
      };
    }

    if (
      isOpen &&
      optionsContainerRef.current?.parentElement &&
      scrollbarRef.current
    ) {
      scrollbarRef?.current?.scrollTo(0, selectedIndex * 50, 200);
    }
  }, [isOpen, selectedIndex]);

  useEffect(() => {
    if (selectedIndex !== -1)
      defaultValue = options.find((item, index) => index === selectedIndex);
    if (defaultValue) {
      setSelectedItem(defaultValue);
      setSelectedIndex(options.findIndex((item) => item === defaultValue));
    }
  }, [defaultValue, options, selectedIndex]);

  return control && name ? (
    <Controller
      control={control}
      name={name}
      onFocus={() => {
        onError &&
          refOuter?.current?.offsetTop &&
          onError(refOuter?.current?.offsetTop - 20);
      }}
      render={({ onChange: onControllerChange, value: selectedValue }) => {
        return (
          <StyledOuterContainer disabled={disabled} ref={refOuter}>
            <StyledInnerContainer
              error={error}
              isOpen={isOpen}
              onBlur={() => setIsOpen(false)}
              onFocus={() => {
                !disabled && setIsOpen(true);
              }}
              onKeyDown={(e) => pressKeyHandler(e, onControllerChange)}
              onMouseDown={(e) => {
                e.currentTarget?.focus();
                e.stopPropagation();
              }}
              // selectedValue={selectedValue}
              selectedValue={selectedItem?.value}
            >
              <SelectScrollWrapper isOpen={isOpen} tabIndex={0}>
                <StyledSelectContainer
                  disabled={disabled}
                  error={error}
                  isOpen={isOpen}
                  onMouseDown={handleSelectClick}
                >
                  {/* <StyledSelectedValue disabled={disabled}>
                    {
                      options?.find((item) => item.value === selectedValue)
                        ?.label
                    }
                  </StyledSelectedValue> */}
                  <StyledSelectedValue>
                    {
                      options?.find(
                        (item) => item.value === selectedItem?.value
                      )?.label
                    }
                  </StyledSelectedValue>

                  {!disabled && (
                    <StyledIconContainer>
                      {isOpen ? (
                        <Image
                          alt='Rotate Icon'
                          height='24px'
                          rotate={90}
                          srcSet={ICONS.LEFT}
                          width='24px'
                        />
                      ) : (
                        <Image
                          alt='Rotate Icon'
                          height='24px'
                          rotate={90}
                          srcSet={ICONS.RIGHT}
                          width='24px'
                        />
                      )}
                    </StyledIconContainer>
                  )}
                </StyledSelectContainer>

                {isOpen && (
                  <ComponentScrollbar ref={scrollbarRef}>
                    <StyledOptionsContainer
                      isOpen={isOpen}
                      ref={optionsContainerRef}
                      selectedIndex={selectedIndex}
                    >
                      {options.length &&
                        options.map((item: OptionProps, index: number) => {
                          return (
                            <StyledOption
                              key={`${item.label}-${item.value}`}
                              isOpen={isOpen}
                              onMouseDown={() => {
                                handleOptionClick(index);
                                onControllerChange(item.value);
                                onChange && onChange(item);
                              }}
                            >
                              {item.label}
                            </StyledOption>
                          );
                        })}
                    </StyledOptionsContainer>
                  </ComponentScrollbar>
                )}
              </SelectScrollWrapper>

              {!!label?.length && <StyledLabel>{label}</StyledLabel>}
            </StyledInnerContainer>

            <StyledErrorLabel>{error}</StyledErrorLabel>
          </StyledOuterContainer>
        );
      }}
    />
  ) : (
    <StyledOuterContainer>
      <StyledInnerContainer
        error={error}
        isOpen={isOpen}
        onBlur={() => setIsOpen(false)}
        onFocus={() => {
          !disabled && setIsOpen(true);
        }}
        onKeyDown={(e) => pressKeyHandler(e)}
        onMouseDown={(e) => {
          e.currentTarget?.focus();
          e.stopPropagation();
        }}
        selectedValue={selectedItem?.value}
      >
        <SelectScrollWrapper
          isOpen={isOpen}
          ref={selectScrollWrapperRef}
          tabIndex={0}
        >
          <StyledSelectContainer
            disabled={disabled}
            error={error}
            isOpen={isOpen}
            onMouseDown={handleSelectClick}
          >
            <StyledSelectedValue>
              {
                options?.find((item) => item.value === selectedItem?.value)
                  ?.label
              }
            </StyledSelectedValue>

            <StyledIconContainer>
              {isOpen ? (
                <Image
                  alt='Rotate Grey'
                  height='24px'
                  rotate={90}
                  srcSet={ICONS.LEFT}
                  width='24px'
                />
              ) : (
                <Image
                  alt='Rotate Grey'
                  height='24px'
                  rotate={90}
                  srcSet={ICONS.RIGHT}
                  width='24px'
                />
              )}
            </StyledIconContainer>
          </StyledSelectContainer>

          {isOpen && (
            <ComponentScrollbar ref={scrollbarRef}>
              <StyledOptionsContainer
                isOpen={isOpen}
                ref={optionsContainerRef}
                selectedIndex={selectedIndex}
              >
                {options.length &&
                  options.map((item: OptionProps, index: number) => {
                    return (
                      <StyledOption
                        key={`${item.label}-${item.value}`}
                        isOpen={isOpen}
                        onMouseDown={() => {
                          handleOptionClick(index);
                          setSelectedItem(item);
                          onChange && onChange(item);
                        }}
                      >
                        {item.label}
                      </StyledOption>
                    );
                  })}
              </StyledOptionsContainer>
            </ComponentScrollbar>
          )}
        </SelectScrollWrapper>

        {!!label?.length && <StyledLabel>{label}</StyledLabel>}
      </StyledInnerContainer>

      <StyledErrorLabel>{error}</StyledErrorLabel>
    </StyledOuterContainer>
  );
};

export default ComboBox;
